/**
 * Admin impersonation RPC definitions (CLA-683).
 *
 * A single admin-gated endpoint backing the "Sign in as user" feature:
 *
 *  - `admin/impersonate/start` — given a `personId`, mints a Clerk **actor
 *    token** for that person on the request's brand Clerk instance, writes an
 *    `impersonation_events` audit row, and returns the token's `url` (the Clerk
 *    Frontend API URL the browser navigates to in order to establish the
 *    impersonated session) plus the `actorTokenId` (retained for a later revoke
 *    path).
 *
 * The endpoint stacks two middlewares: {@link AdminMiddleware} enforces the
 * `admin` role and provides the acting {@link CurrentActor} (whose
 * `person.clerkId` becomes the actor `sub`), and {@link BrandMiddleware} resolves
 * the request host to the brand whose Clerk instance mints the token. The group
 * is `.prefix`ed with `admin/impersonate/` (slash convention) so the generated
 * RPC client exposes a flat `rpc["admin/impersonate/start"]` method.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { BrandMiddleware } from "../brand.tags.ts";
import { DatabaseError, Forbidden, ImpersonationError, RecordNotFoundError, Unauthorized } from "../errors.ts";

/**
 * Start-impersonation input. `personId` is the `people.id` of the person to
 * impersonate; `reason` is an optional support note recorded on the audit row.
 *
 * @since 1.0.0
 */
export const ImpersonateStartInput = Schema.Struct({
  personId: Schema.String,
  reason: Schema.optional(Schema.String),
});
export type ImpersonateStartInput = Schema.Schema.Type<typeof ImpersonateStartInput>;

/**
 * Start-impersonation result. `url` is the Clerk Frontend API URL that signs out
 * the current user and redirects into the sign-in-ticket flow; `actorTokenId` is
 * the Clerk actor-token id retained for a later revoke path.
 *
 * @since 1.0.0
 */
export const ImpersonateStartResult = Schema.Struct({
  url: Schema.String,
  actorTokenId: Schema.String,
});
export type ImpersonateStartResult = Schema.Schema.Type<typeof ImpersonateStartResult>;

/**
 * Admin impersonation RPC group. `start` requires the `admin` role via
 * {@link AdminMiddleware} and resolves the brand via {@link BrandMiddleware}.
 *
 * @since 1.0.0
 */
export class AdminImpersonateRpcs extends RpcGroup.make(
  Rpc.make("start", {
    payload: ImpersonateStartInput,
    success: ImpersonateStartResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ImpersonationError, DatabaseError),
  }).middleware(AdminMiddleware).middleware(BrandMiddleware),
).prefix("admin/impersonate/") {}
